import { DiagnosticsQueryParams, FilterType, QueryType, Severity } from "../types/dialog-types";
import { TIGER_TO_UNIX_OFFSET_SECS, getTigerLangOrdinal } from "./tigerTime";

/// Rust-side DiagQueryParams shape (matches lib_tcoms_rs's struct after the
/// serde::Deserialize derive landed in Sub-spec C, Task 1).
export interface DiagQueryParams {
  source: string;
  start_time: number;
  end_time: number;
  config_names: string[];
  message_id_patterns: string[];
  message_text_patterns: string[];
  area_patterns: string[];
  tag_patterns: string[];
  state_ids: string[];
  severity: number[];
  latch_level: number[];
  query_type: number[];
  query_id: number[];
  filter_type: number[];
  group_turbine: number[];
  failed_sensor: number[];
  skipall: number;
  tiger_lang: number;
  engine_name: string;
}

// Severity bitmask values from `libTiger/pc_common.h:94-121`.
const SEVERITY_BITS: Record<string, number> = {
  Trip: 0x01,
  Critical: 0x02,
  Fault: 0x04,
  Warning: 0x08,
  Event: 0x10,
  Information: 0x20,
};

// Controller-alarm severity bits (pc_common.h). The dialog's per-source
// checkboxes (Speedtronic/Sentinel/OPC) map onto these; without them the server
// never returns controller-alarm messages.
const MARK_PALARM_BITS = 0x100 | 0x200; // ON | OFF
const MARK_DALARM_BITS = 0x400 | 0x800;
const MARK_EVENT_BITS = 0x1000 | 0x2000;
const OPC_ALARM_BITS = 0x4000 | 0x8000;
const OPC_EVENT_BIT = 0x10000;
const SENTINEL_TRIP_BIT = 0x20000;
const SENTINEL_ALARM_BIT = 0x40000;

// QUERY_TYPE enum from `libTiger/pc_common.h:123`. Values match the C++ enum
// in declaration order (0-indexed, no explicit values).
const QUERY_TYPE_MAP: Record<string, number> = {
  "Complete Listing": 0, // QUERY_TYPE_TIME
  "Top Level": 1, // QUERY_TYPE_TOPLEVEL
  "Number of Occurrences": 2, // QUERY_TYPE_NBOCCURS
  "Diagnostic Area": 3, // QUERY_TYPE_AREA
  "Currently Abnormal": 4, // QUERY_TYPE_OUTSTANDING
  "Diagnostics List": 6, // QUERY_TYPE_DIAG_LIST
  Invalid: 8, // QUERY_TYPE_INVALID_TAG
};

// When "Group results per Area" is on, each base query type maps to its
// area-grouped variant (pc_common.h QUERY_TYPE enum). The server then returns
// per-area top-level nodes ("Reports in area …" / "Alarm Reports").
const AREA_QUERY_VARIANT: Record<number, number> = {
  0: 3, // TIME → AREA
  1: 12, // TOPLEVEL → AREA_TOP
  2: 7, // NBOCCURS → AREA_OCC
  4: 9, // OUTSTANDING → AREA_OUT
  6: 10, // DIAG_LIST → AREA_DIAG
  8: 11, // INVALID_TAG → AREA_INVALID
};

const FILTER_TYPE_MAP: Record<string, number> = {
  Off: 0,
  On: 1,
  Inverse: 2,
};

// Convert a dialog date ("YYYY-MM-DD") + optional time ("HH:MM") into Tiger
// seconds (since 1901-01-01) — the W_DIAG_QUERY_MSG wire fields 4/5 are RWTime
// values (the C++ wraps them straight into RWTime(query_start_time),
// diagnostics_display.cpp:2507), NOT Unix seconds. Interpreted as UTC to match
// the rest of the app.
//
// The dialog only exposes Start Date / End Date (no time-of-day fields), so
// when the time is empty we fill it in the way the C++ does
// (diagnostics_dialog.cpp:6808/6814): the start is the start of its day
// (00:00:00) and the end is the end of its day (23:59:59). Without this the
// end defaulted to 00:00:00, so a same-day query was a zero-width window —
// returning no rows — and any range silently excluded the whole end day.
function dateTimeToTigerSeconds(
  dateStr: string,
  timeStr: string,
  isEnd: boolean,
): number {
  if (!dateStr) return 0;
  // An explicit "HH:MM" gets seconds appended; an empty field becomes the
  // start (00:00:00) or end (23:59:59) of the day.
  const t = timeStr ? `${timeStr}:00` : isEnd ? "23:59:59" : "00:00:00";
  const d = new Date(`${dateStr}T${t}Z`);
  return Math.floor(d.getTime() / 1000) + TIGER_TO_UNIX_OFFSET_SECS;
}

/// Build the pipe-delimited `key=value` template metadata string the C++ embeds
/// in the "Tiger Diagnostics Template" comment when saving as .xml / .fodt
/// (mirrors `diagnostics_shell_c::create_xml_tool_prop` +
/// `xml_tool_prop_set::get_xml_string`). Keys and order match the C++ exactly.
///
/// Fields the Tauri port has no equivalent for — the MFC window rectangle
/// (top/bottom/left/right), the display flags and the occurrence-sort type — are
/// emitted with their neutral defaults; they don't affect the query a re-import
/// would run. `turbine_state` is likewise 0 (the port carries the state by name,
/// not the C++ enum ordinal).
export function buildDiagTemplateMeta(ui: DiagnosticsQueryParams): string {
  const sev = severityUiToMask(ui);

  // Base query type (not the area-grouped variant — the C++ saves my_query_type).
  const queryType = QUERY_TYPE_MAP[ui.queryType] ?? 1;
  const filterType = FILTER_TYPE_MAP[ui.filterType] ?? 0;
  const latch = parseInt(ui.latchCount || "0", 10) || 0;

  const pairs: [string, string][] = [
    ["dialog_type", "diagnostics"],
    ["top", "0"],
    ["bottom", "0"],
    ["left", "0"],
    ["right", "0"],
    ["diagnostics_font", "Arial Narrow"],
    ["diagnostics_font_size", "20"],
    ["diagnostics_type", "0"],
    ["display_id", "0"],
    ["display_la_in_tq", "0"],
    ["num_occur_sort_type", "0"],
    ["expand_level", "0"],
    ["query_specified", "1"],
    ["query_turbine", ui.turbine || ""],
    ["query_id", ui.messageId || ""],
    ["query_msg", ui.messageText || ""],
    ["query_area", ui.messageArea || ""],
    ["query_sev", String(sev)],
    ["query_ll", String(latch)],
    ["query_grp", ui.groupTurbine ? "1" : "0"],
    ["failed_sensor", ui.failedSensor ? "1" : "0"],
    ["filter_type", String(filterType)],
    ["turbine_state", "0"],
    ["query_type", String(queryType)],
    ["hist", ui.isHistorical ? "1" : "0"],
    ["hist_start_time", "report_start_time"],
    ["hist_end_time", "report_end_time"],
  ];
  return pairs.map(([k, v]) => `${k}=${v}`).join("|");
}

/** Fold the dialog's severity names + per-source alarm checkboxes into the
 *  pc_common.h severity bitmask. Shared by the query build, the template
 *  metadata, and tool-set saving. */
export function severityUiToMask(
  ui: Pick<DiagnosticsQueryParams, "severity" | "speedtronicAlarms" | "sentinelAlarms" | "opcAlarms">,
): number {
  let mask = ui.severity.reduce((acc, s) => acc | (SEVERITY_BITS[s] ?? 0), 0) | 0;
  if (ui.speedtronicAlarms.palarm) mask |= MARK_PALARM_BITS;
  if (ui.speedtronicAlarms.dalarm) mask |= MARK_DALARM_BITS;
  if (ui.speedtronicAlarms.event) mask |= MARK_EVENT_BITS;
  if (ui.opcAlarms.alarm) mask |= OPC_ALARM_BITS;
  if (ui.opcAlarms.event) mask |= OPC_EVENT_BIT;
  if (ui.sentinelAlarms.trip) mask |= SENTINEL_TRIP_BIT;
  if (ui.sentinelAlarms.alarm) mask |= SENTINEL_ALARM_BIT;
  return mask;
}

/** The QUERY_TYPE ordinal for a dialog query-type name — the area-grouped
 *  variant when "Group results per Area" is on (matching what the C++ runs and
 *  saves in its tool sets). */
export function queryTypeToOrdinal(name: QueryType, groupArea: boolean): number {
  const base = QUERY_TYPE_MAP[name] ?? 1;
  return groupArea ? AREA_QUERY_VARIANT[base] ?? base : base;
}

/** The filter-type ordinal for a dialog filter name (Off/On/Inverse). */
export function filterTypeToOrdinal(name: FilterType): number {
  return FILTER_TYPE_MAP[name] ?? 0;
}

/** Dialog date ("YYYY-MM-DD") + optional time ("HH:MM") → Tiger seconds; see
 *  `dateTimeToTigerSeconds` for the end-of-day handling. Exported for tool-set
 *  saving, which stores the historical window as RWTime values. */
export function uiDateTimeToTigerSeconds(dateStr: string, timeStr: string, isEnd: boolean): number {
  return dateTimeToTigerSeconds(dateStr, timeStr, isEnd);
}

/** Reverse of the severity folding in `buildDiagQueryParams`: split a saved
 *  severity bitmask (e.g. from a tool set's `query_sev`) back into the dialog's
 *  severity names and per-source alarm checkboxes. */
export function severityMaskToUi(
  mask: number,
): Pick<DiagnosticsQueryParams, "severity" | "speedtronicAlarms" | "sentinelAlarms" | "opcAlarms"> {
  const severity = (Object.keys(SEVERITY_BITS) as Severity[]).filter(
    (s) => (mask & SEVERITY_BITS[s]) !== 0,
  );
  return {
    severity,
    speedtronicAlarms: {
      palarm: (mask & MARK_PALARM_BITS) !== 0,
      dalarm: (mask & MARK_DALARM_BITS) !== 0,
      event: (mask & MARK_EVENT_BITS) !== 0,
    },
    sentinelAlarms: {
      trip: (mask & SENTINEL_TRIP_BIT) !== 0,
      alarm: (mask & SENTINEL_ALARM_BIT) !== 0,
    },
    opcAlarms: {
      alarm: (mask & OPC_ALARM_BITS) !== 0,
      event: (mask & OPC_EVENT_BIT) !== 0,
    },
  };
}

/** Reverse of `QUERY_TYPE_MAP` (+ area variants): a saved QUERY_TYPE ordinal
 *  back to the dialog's query-type name and "Group results per Area" flag.
 *  Unknown ordinals fall back to Top Level (the dialog default). */
export function queryTypeFromOrdinal(n: number): { queryType: QueryType; groupArea: boolean } {
  for (const [name, base] of Object.entries(QUERY_TYPE_MAP)) {
    if (base === n) return { queryType: name as QueryType, groupArea: false };
    if (AREA_QUERY_VARIANT[base] === n) return { queryType: name as QueryType, groupArea: true };
  }
  return { queryType: "Top Level", groupArea: false };
}

/** Reverse of `FILTER_TYPE_MAP`; unknown ordinals fall back to Off. */
export function filterTypeFromOrdinal(n: number): FilterType {
  const hit = Object.entries(FILTER_TYPE_MAP).find(([, v]) => v === n);
  return (hit?.[0] as FilterType) ?? "Off";
}

export function buildDiagQueryParams(
  ui: DiagnosticsQueryParams,
  allConfigs?: string[],
  siteConfigName?: string,
): DiagQueryParams {
  // Severity names + per-source alarm checkboxes folded into one mask so the
  // server actually returns controller-alarm (Speedtronic/Sentinel/OPC) rows.
  const severityMask = severityUiToMask(ui);
  // Only an explicit Historical query carries a time window; online queries send
  // 0/0 so the server returns the current (live) diagnostics.
  const start = ui.isHistorical
    ? dateTimeToTigerSeconds(ui.timeRange.startDate, ui.timeRange.startTime, false)
    : 0;
  const end = ui.isHistorical
    ? dateTimeToTigerSeconds(ui.timeRange.endDate, ui.timeRange.endTime, true)
    : 0;

  // Empty turbine = site-view sentinel. In a turbine-scoped window (a web
  // `?live=<config>` tab scopes the turbine list to that one config) there is
  // no site view — "" must resolve to that turbine, NEVER the site name: the
  // diag daemon fans a site-name query out to EVERY turbine on the site (and a
  // historical one then RDD-downloads them all). Only a genuine multi-turbine
  // view issues a single site-wide query using the site config name (one
  // request) rather than one query per turbine; fall back to expanding every
  // config if the site name is unavailable.
  const configNames = ui.turbine
    ? [ui.turbine]
    : allConfigs && allConfigs.length === 1
      ? [allConfigs[0]]
      : siteConfigName
        ? [siteConfigName]
        : allConfigs && allConfigs.length > 0
          ? allConfigs
          : [""];

  const baseQueryType = QUERY_TYPE_MAP[ui.queryType] ?? 1;
  const queryType = ui.groupArea
    ? AREA_QUERY_VARIANT[baseQueryType] ?? baseQueryType
    : baseQueryType;

  // Every per-query array must hold one entry PER config name: the C++
  // diag_collector extracts each array with number_of_queries elements and its
  // getters return uninitialized memory when that extract fails, so a ragged
  // message (N config names, 1 query_id) hangs the site-wide query on its
  // timeout. Same criteria replicated per turbine.
  const rep = <T,>(value: T): T[] => configNames.map(() => value);

  // Source string mirrors the C++ diagnostics_display.cpp:2318-2333: a Historical
  // query is "QUERY" (server reads its historical buffer over the window), any
  // other query is "ONLINE". In replay the backend (submit_diag_query) maps that
  // ONLINE to "OFFLINE" so the query is served from the replay session.
  return {
    source: ui.isHistorical ? "QUERY" : "ONLINE",
    start_time: start,
    end_time: end,
    config_names: configNames,
    message_id_patterns: rep(ui.messageId || ""),
    message_text_patterns: rep(ui.messageText || ""),
    area_patterns: rep(ui.messageArea || ""),
    tag_patterns: rep(ui.tagName || ""),
    state_ids: rep(ui.turbineState === "All States" ? "" : ui.turbineState),
    severity: rep(severityMask),
    latch_level: rep(parseInt(ui.latchCount || "25", 10) || 25),
    query_type: rep(queryType),
    query_id: rep(0),
    filter_type: rep(FILTER_TYPE_MAP[ui.filterType] ?? 0),
    group_turbine: rep(ui.groupTurbine ? 1 : 0),
    failed_sensor: rep(ui.failedSensor ? 1 : 0),
    skipall: 0,
    // The site's TIGER_LANG, as the C++ UI sends it. The diagnostics buffer
    // applies this to its process language for the query
    // (`buffer/run.rs` set_tiger_lang), and `single_message::get_text_message`
    // then returns the English default for LANG_UK and the localised template
    // otherwise — so a hard-coded 0 here brought every reply back in English
    // however the config was set up.
    tiger_lang: getTigerLangOrdinal(),
    engine_name: "",
  };
}
